// At-a-glance KPI row: 5 tiles (Pass Rate, Evaluated, Below Threshold,
// Average Score, With Failed Criteria) — each carries metric + delta +
// (on scope tabs) cohort rank.
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'

import { Card } from '@/components/cards/Card'
import { EChart } from '@/components/charts/EChart'

import { CHANNEL_TYPE, CHANNEL_NAME_PLURAL_DISPLAY } from '@/utils/constants'
import { CHART } from '@/components/charts/chartPalette'
import { sumDailyCalls, weightedAvgScore } from '../helpers/format'

import { ExternalCallIdsLink } from '../../../components/ExternalCallIdsLink'
import { useCohortContext } from '../helpers/cohort'

// Pass rate from the absolute counts when we don't have a cohort-
// derived pass rate. Returns null when the inputs aren't available
// or when the denominator is zero.
const computeFallbackPassRate = (evaluatedCount, belowThresholdCount) => {
  if (isNil(evaluatedCount) || evaluatedCount === 0) {
    return null
  }
  if (isNil(belowThresholdCount)) {
    return null
  }
  return (100 * (evaluatedCount - belowThresholdCount)) / evaluatedCount
}

// Tiny pluralizer so the formatDelta call sites don't carry inline
// ternaries for "1 call" vs "N calls".
const pluralizeUnit = (count, singular, plural) =>
  !isNil(count) && Math.abs(count) === 1 ? singular : plural

const buildSparklineOption = (values, color) => ({
  tooltip: { show: false },
  grid: { left: 0, right: 0, top: 2, bottom: 2 },
  xAxis: {
    type: 'category',
    show: false,
    boundaryGap: false,
    data: values.map((_, index) => index),
  },
  yAxis: { type: 'value', show: false, scale: true },
  series: [
    {
      type: 'line',
      data: values,
      smooth: true,
      showSymbol: false,
      lineStyle: { color, width: 2 },
      itemStyle: { color },
      areaStyle: { color, opacity: 0.12 },
      silent: true,
    },
  ],
})

const formatDelta = (value, suffix, decimals, goodWhenUp) => {
  if (isNil(value)) {
    return null
  }
  const rounded = Number(value.toFixed(decimals))
  if (rounded === 0) {
    return { text: `— ${suffix}`, tone: 'neutral' }
  }
  const up = rounded > 0
  const tone = up === goodWhenUp ? 'good' : 'bad'
  return {
    text: [up ? '▲' : '▼', ` ${Math.abs(rounded)}`, suffix ? ` ${suffix}` : ''].join(''),
    tone,
  }
}

// A single at-a-glance tile. All formatting lives here; the parent
// hands in fully-prepared text for value/delta/cohort lines.
//
// When `callIds` is non-empty the value renders as a Call Explorer
// link -- matches the prod OverviewCard behavior so clicking the
// number jumps to the underlying call list.
const AtAGlanceTile = ({
  testId,
  title,
  value,
  loading,
  callIds,
  channelType,
  deltaText,
  deltaTone,
  cohortLines,
  sparklineValues,
  sparklineColor,
}) => {
  const sparklineOption = useMemo(
    () =>
      !isEmpty(sparklineValues) ? buildSparklineOption(sparklineValues, sparklineColor) : null,
    [sparklineValues, sparklineColor]
  )
  const hasLink = Array.isArray(callIds) && !isEmpty(callIds)
  return (
    <Card>
      <div className="overview-tile">
        <div className="overview-tile-title">{title}</div>
        <div className="overview-tile-value" data-testid={`kpi-${testId}-value`}>
          {loading && '…'}
          {!loading && hasLink && (
            <ExternalCallIdsLink callIds={callIds} channelType={channelType} label={value} />
          )}
          {!loading && !hasLink && value}
        </div>
        {deltaText && (
          <div
            className={classNames(
              'overview-tile-delta',
              `overview-tile-delta-${deltaTone || 'neutral'}`
            )}
          >
            <span>{deltaText}</span>
          </div>
        )}
        {!isEmpty(cohortLines) && (
          <div className="overview-tile-cohort">
            {cohortLines.map((line) => (
              <div key={line} className="overview-tile-cohort-line">
                {line}
              </div>
            ))}
          </div>
        )}
        {sparklineOption && (
          <div className="overview-tile-sparkline" data-testid="kpi-sparkline">
            <EChart option={sparklineOption} style={{ height: '32px', width: '100%' }} />
          </div>
        )}
      </div>
    </Card>
  )
}

export const OverviewCardsRow = ({ scope }) => {
  const { data, loading } = useSelector((state) => state.qaCopilot)
  const cohort = useCohortContext(scope)

  const appliedCallType = data.appliedCallType || CHANNEL_TYPE.CALL
  const channelLabel = CHANNEL_NAME_PLURAL_DISPLAY[appliedCallType]

  const aggregates = data.reportingByDay?.aggregates || []
  const callsByDay = data.reportingByDay?.calls || []
  const scoreSeries = useMemo(
    () => aggregates.map((row) => parseFloat(row?.['QA Score']) || 0),
    [aggregates]
  )

  // Prior-window rollup comes from redux -- populated by the dashboard
  // bootstrap action alongside the current window in a single round trip.
  // Failed bundle sections resolve to null and the deltas just hide.
  const priorByDay = data.reportingByDayPrior

  // --- Derived metrics ---
  const evaluatedCount = data.evaluatedCallData?.call_count
  const belowThresholdCount = data.failedCallData?.call_count
  const failedCriteriaCount = data.failedCriteriaCallData?.call_count
  const currentAvgScore = data.reporting?.aggregate_scores?.total

  // Prefer the cohort-derived pass rate (so the headline matches the
  // rank/cohort-avg lines below it) -- but ONLY when the cohort metric
  // actually IS a pass rate. On By Scorecard the cohort carries avg QA
  // score; showing it under a "Pass Rate" tile silently switched
  // metrics. Fall back to (evaluated - below_threshold) / evaluated.
  const passRate = useMemo(
    () =>
      (cohort.metricIsPassRate ? cohort.selectedPassRate : null) ??
      computeFallbackPassRate(evaluatedCount, belowThresholdCount),
    [cohort.metricIsPassRate, cohort.selectedPassRate, evaluatedCount, belowThresholdCount]
  )

  const priorAvgScore = useMemo(
    () => weightedAvgScore(priorByDay?.aggregates, priorByDay?.calls),
    [priorByDay]
  )
  const scoreDelta = useMemo(() => {
    if (!priorByDay || isNil(priorAvgScore) || isNil(currentAvgScore)) {
      return null
    }
    return parseFloat(currentAvgScore) - priorAvgScore
  }, [priorByDay, priorAvgScore, currentAvgScore])

  const currentTotalCalls = useMemo(() => sumDailyCalls(callsByDay), [callsByDay])
  const priorTotalCalls = useMemo(() => sumDailyCalls(priorByDay?.calls), [priorByDay])
  const callsDelta = useMemo(() => {
    if (!priorByDay || isEmpty(priorByDay.calls)) {
      return null
    }
    return currentTotalCalls - priorTotalCalls
  }, [priorByDay, currentTotalCalls, priorTotalCalls])

  // --- Cohort lines per tile ---
  const passRateCohortLines = useMemo(() => {
    if (!cohort.isAvailable || !cohort.pickedEntity) {
      return null
    }
    // Name the metric when it isn't a pass rate (By Scorecard ranks by
    // avg QA score) so the lines can't be misread against the tile.
    const suffix = cohort.metricIsPassRate ? '' : ` by ${cohort.metricLabel}`
    const lines = []
    if (cohort.passRateRank) {
      lines.push(`Rank ${cohort.passRateRank.rank} of ${cohort.passRateRank.total}${suffix}`)
    }
    if (!isNil(cohort.cohortAvg)) {
      lines.push(`avg ${cohort.cohortAvg.toFixed(1)}%${suffix}`)
    }
    return isEmpty(lines) ? null : lines
  }, [cohort])

  const volumeCohortLines = useMemo(() => {
    if (!cohort.isAvailable || !cohort.pickedEntity || !cohort.volumeRank) {
      return null
    }
    return [`Rank ${cohort.volumeRank.rank} of ${cohort.volumeRank.total}`]
  }, [cohort])

  // --- Tile deltas ---
  // Pass-rate delta on a scope tab compares to cohort avg (since the
  // cohort line carries the bigger story); on Overview it has no
  // prior-period equivalent from the existing redux data, so we omit.
  const passRateDelta =
    cohort.isAvailable && !isNil(cohort.passDelta)
      ? formatDelta(cohort.passDelta, 'points vs average', 1, true)
      : null
  const evaluatedDelta = formatDelta(
    callsDelta,
    `${pluralizeUnit(callsDelta, 'call', 'calls')} vs prior`,
    0,
    true
  )
  const scoreDeltaFmt = formatDelta(scoreDelta, 'points vs prior', 1, true)

  // Tile configs -- keep tile order + wiring in ONE place so adding /
  // reordering a headline number is a single-line diff. Each entry is
  // (props for AtAGlanceTile). Nils and undefineds get filtered out of
  // the render below so we can express conditional props concisely.
  const tiles = [
    {
      key: 'pass-rate',
      title: 'Pass Rate',
      value: isNil(passRate) ? '—' : `${passRate.toFixed(1)}%`,
      // Spinner only while we genuinely lack inputs -- previously
      // chained several redux loading flags and got stuck on "...".
      loading: isNil(passRate) && (cohort.loading || isNil(data.evaluatedCallData)),
      // Click the pass rate to inspect the FAILING calls -- the
      // actionable subset for a coach.
      callIds: data.failedCallData?.call_ids,
      channelType: appliedCallType,
      deltaText: passRateDelta?.text,
      deltaTone: passRateDelta?.tone,
      cohortLines: passRateCohortLines,
    },
    {
      key: 'evaluated',
      title: `Evaluated ${channelLabel}`,
      value: isNil(evaluatedCount) ? '—' : evaluatedCount.toLocaleString(),
      loading: loading.evaluatedCallData,
      callIds: data.evaluatedCallData?.call_ids,
      channelType: appliedCallType,
      deltaText: evaluatedDelta?.text,
      deltaTone: evaluatedDelta?.tone,
      cohortLines: volumeCohortLines,
    },
    {
      key: 'below-threshold',
      title: `${channelLabel} Below Threshold`,
      value: isNil(belowThresholdCount) ? '—' : belowThresholdCount.toLocaleString(),
      loading: loading.failedCallData,
      callIds: data.failedCallData?.call_ids,
      channelType: appliedCallType,
    },
    {
      key: 'average-score',
      title: 'Average Score',
      value: isNil(currentAvgScore) ? '—' : `${parseFloat(currentAvgScore).toFixed(2)}%`,
      loading: loading.reporting,
      callIds: data.evaluatedCallData?.call_ids,
      channelType: appliedCallType,
      deltaText: scoreDeltaFmt?.text,
      deltaTone: scoreDeltaFmt?.tone,
      sparklineValues: scoreSeries,
      sparklineColor: CHART.BRAND,
    },
    {
      key: 'failed-criteria',
      title: `${channelLabel} with Failed Criteria`,
      value: isNil(failedCriteriaCount) ? '—' : failedCriteriaCount.toLocaleString(),
      loading: loading.failedCriteriaCallData,
      callIds: data.failedCriteriaCallData?.call_ids,
      channelType: appliedCallType,
    },
  ]

  return (
    <div className="copilot-overview-cards dashboard-overview-cards">
      {tiles.map(({ key, ...tileProps }) => (
        <AtAGlanceTile key={key} testId={key} {...tileProps} />
      ))}
    </div>
  )
}
